import API_BASE_URL from '@/config/api'

/**
 * User information interface (from Cloudflare Access)
 */
export interface User {
  email: string
  name: string
  sub: string
  authenticated: boolean
  authMethod: string
}

/**
 * Authentication service for Cloudflare Access
 * No JWT tokens needed - Cloudflare handles authentication via cookies
 */
class AuthService {
  private readonly USER_KEY = 'calmren_user'
  private userCache: User | null = null

  /**
   * Check if user is authenticated by calling the API
   * Cloudflare Access will handle the authentication
   */
  async checkAuth(): Promise<User | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user`, {
        credentials: 'include', // Important: include cookies
      })

      if (!response.ok) {
        this.clearAuth()
        return null
      }

      const data = await response.json()
      const user = data.user

      // Cache user info
      this.setUser(user)
      this.userCache = user

      return user
    } catch (error) {
      console.error('Auth check error:', error)
      this.clearAuth()
      return null
    }
  }

  /**
   * Logout user - redirect to Cloudflare Access logout
   */
  async logout(): Promise<void> {
    try {
      // Clear local cache
      this.clearAuth()

      // Redirect to Cloudflare Access logout
      // This will clear the Cloudflare Access session
      window.location.href = `https://calmren.cloudflareaccess.com/cdn-cgi/access/logout`
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  /**
   * Clear authentication data
   */
  clearAuth(): void {
    localStorage.removeItem(this.USER_KEY)
    // Clean up old JWT tokens if they exist
    localStorage.removeItem('calmren_token')
    localStorage.removeItem('calmren_auth')
    this.userCache = null
  }

  /**
   * Check if user is authenticated (from cache)
   * Call checkAuth() to verify with server
   */
  isAuthenticated(): boolean {
    return !!this.getUser()
  }

  /**
   * Get current user from cache
   */
  getUser(): User | null {
    if (this.userCache) {
      return this.userCache
    }

    const userStr = localStorage.getItem(this.USER_KEY)
    if (!userStr) return null

    try {
      this.userCache = JSON.parse(userStr)
      return this.userCache
    } catch {
      return null
    }
  }

  /**
   * Store user info
   */
  setUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user))
    this.userCache = user
  }
}

export const authService = new AuthService()
export default authService
